import React, { useState, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import API from '../../services/api'
import { debounce } from '../../utils/Debounce'
import Paginator from '../../shared/Paginator'
import TableLoader from '../../shared/TableLoader'
import NoDataFound from '../../shared/NoDataFound'
import { Toast } from '../../shared/Toast'
import { SlInput, SlIcon } from '@shoelace-style/shoelace/dist/react'
import EditableCell, { selectProps } from './EditableCell'

function LineitemEditor() {
    const [lineitems, setLineitems] = useState([])
    const [loader, setLoader] = useState(false)
    const [totalPages, setTotalPages] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(0)
    const [search, setSearch] = useState('')

    //total budget edit details
    const [totalBudgetEditIndex, setTotalBudgetEditIndex] = useState(-1)
    const [totalBudgetEditValue, setTotalBudgetEditValue] = useState(null)

    //cpd edit details
    const [cpdEditIndex, setCpdEditIndex] = useState(-1)
    const [cpdEditValue, setCpdEditValue] = useState(null)

    //brand edit details
    const [brandEditIndex, setBrandEditIndex] = useState(-1)
    const [brandEditOption, setBrandEditOption] = useState(null)

    const resetEditors = () => {
        setTotalBudgetEditIndex(-1)
        setTotalBudgetEditValue(null)
        setCpdEditIndex(-1)
        setCpdEditValue(null)
        setBrandEditIndex(-1)
        setBrandEditOption(null)
    }

    const getData = async () => {
        setLoader(true)
        let res = await API({
            url: '/noc-updates/get-lineitems',
            method: 'POST',
            data: {
                page: currentPage,
                limit: 10,
                search: search
            }
        })
        if (res.code === 200) {
            setLineitems(res.data.lineitems)
            setTotalCount(res.data.total)
            setTotalPages(Math.ceil(res.data.total / 10))
        }
        resetEditors()
        setLoader(false)
    }

    useEffect(() => {
        getData()
    }, [currentPage, search])

    const searchData = (e) => {
        setSearch(e.target.value)
        setCurrentPage(0)
    }

    const handlePageClick = (event) => {
        setCurrentPage(event.selected)
    }

    const loadBrands = async (inputValue = "") => {
        if (!inputValue || inputValue.length < 3) {
            return []
        }
        try {
            const res = await API({
                url: '/user/sales_persons/getBrands',
                method: 'POST',
                data: { search: inputValue }
            })
            if (res?.code === 200 && Array.isArray(res?.data)) {
                return res.data.map((item) => ({
                    label: item?.label || item?.brand_name || item?.name || `${item?.value ?? item?.brand_id}`,
                    value: item?.value || item?.brand_id || item?.id,
                }))
            }
            return []
        } catch (error) {
            console.error("Error loading brands", error)
            return []
        }
    }

    const handleLineitemEdit = async (campaign_id, body) => {
        setLoader(true)
        let res = await API({
            url: '/noc-updates/update-lineitem',
            method: 'POST',
            data: { campaign_id, details: body }
        })
        if (res.code === 200) {
            getData()
        }
        setLoader(false)
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
                <p className="mb-0 text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{lineitems?.length || 0}</span> of {totalCount} lineitems
                </p>
                <SlInput
                    size="medium"
                    className="w-72"
                    onSlInput={debounce(searchData, 500)}
                    placeholder="Search by lineitem name…"
                >
                    <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
                </SlInput>
            </div>

            <div className="mt-3 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
                <table className="noc-table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Lineitem Name</th>
                            <th>Brand</th>
                            <th>Total Budget</th>
                            <th>CPD</th>
                        </tr>
                    </thead>
                    {loader ? <TableLoader colspan={5} /> : lineitems?.length > 0 ? (
                        <tbody>
                            {lineitems.map((lineitem) => (
                                <tr key={lineitem.campaign_id}>
                                    <td className="text-slate-500">{lineitem.order_id}</td>
                                    <td className="font-medium text-slate-800">{lineitem.campaign_name}</td>
                                    <td>
                                        <EditableCell
                                            editing={brandEditIndex === lineitem.campaign_id}
                                            display={lineitem.brand_name}
                                            onEdit={() => {
                                                setBrandEditIndex(lineitem.campaign_id)
                                                setBrandEditOption(lineitem.brand_id ? { label: lineitem.brand_name, value: lineitem.brand_id } : null)
                                            }}
                                            onCancel={() => { setBrandEditIndex(-1); setBrandEditOption(null) }}
                                            onSave={() => {
                                                if (!brandEditOption?.value || brandEditOption.value === lineitem.brand_id) {
                                                    Toast("error", "Please select a different brand")
                                                    return
                                                }
                                                handleLineitemEdit(lineitem.campaign_id, { previous_value: lineitem.brand_id, updated_value: brandEditOption.value, type: "brand_id" })
                                            }}
                                        >
                                            <AsyncSelect
                                                {...selectProps}
                                                cacheOptions
                                                loadOptions={loadBrands}
                                                value={brandEditOption}
                                                onChange={(selected) => setBrandEditOption(selected)}
                                                placeholder="Search brand…"
                                                noOptionsMessage={({ inputValue }) =>
                                                    inputValue.length < 3 ? "Type at least 3 characters" : "No brands found"
                                                }
                                            />
                                        </EditableCell>
                                    </td>
                                    <td>
                                        <EditableCell
                                            editing={totalBudgetEditIndex === lineitem.campaign_id}
                                            canEdit={lineitem.campaign_type !== "CPD"}
                                            display={lineitem.total_budget}
                                            onEdit={() => { setTotalBudgetEditIndex(lineitem.campaign_id); setTotalBudgetEditValue(lineitem.total_budget) }}
                                            onCancel={() => { setTotalBudgetEditIndex(-1); setTotalBudgetEditValue(null) }}
                                            onSave={() => {
                                                if (totalBudgetEditValue === lineitem.total_budget) {
                                                    Toast("error", "Please enter a different total budget")
                                                    return
                                                }
                                                handleLineitemEdit(lineitem.campaign_id, { previous_value: lineitem.total_budget, updated_value: totalBudgetEditValue, type: "total_budget" })
                                            }}
                                        >
                                            <input
                                                type="number"
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
                                                value={totalBudgetEditValue ?? ''}
                                                onChange={(e) => setTotalBudgetEditValue(e.target.value)}
                                            />
                                        </EditableCell>
                                    </td>
                                    <td>
                                        <EditableCell
                                            editing={cpdEditIndex === lineitem.campaign_id}
                                            canEdit={lineitem.campaign_type === "CPD"}
                                            display={lineitem.cpd}
                                            onEdit={() => { setCpdEditIndex(lineitem.campaign_id); setCpdEditValue(lineitem.cpd) }}
                                            onCancel={() => { setCpdEditIndex(-1); setCpdEditValue(null) }}
                                            onSave={() => {
                                                if (cpdEditValue === lineitem.cpd) {
                                                    Toast("error", "Please enter a different CPD")
                                                    return
                                                }
                                                handleLineitemEdit(lineitem.campaign_id, { previous_value: lineitem.cpd, updated_value: cpdEditValue, type: "cpd" })
                                            }}
                                        >
                                            <input
                                                type="number"
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
                                                value={cpdEditValue ?? ''}
                                                onChange={(e) => setCpdEditValue(e.target.value)}
                                            />
                                        </EditableCell>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : <NoDataFound colspan={5} />}
                </table>
            </div>

            <div className="mt-3 flex shrink-0 justify-end">
                {totalPages > 1 && (
                    <Paginator
                        pageCount={totalPages}
                        handlePageClick={handlePageClick}
                        forcePage={currentPage}
                    />
                )}
            </div>
        </div>
    )
}

export default LineitemEditor
